import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DONORS } from "@/constants/testIds";

const EMPTY = {
  type: "privato",
  first_name: "", last_name: "", org_name: "",
  email: "", phone: "", whatsapp: "",
  address: "", city: "", country: "Italia",
  referente: "", cf: "", piva: "", notes: "",
  consent_communications: true, consent_newsletter: true,
};

const ORG_TYPES = ["chiesa", "azienda", "associazione", "fondazione"];

function normalize(donor) {
  if (!donor) return { ...EMPTY };
  return {
    type: donor.type || "privato",
    first_name: donor.first_name || "",
    last_name: donor.last_name || "",
    org_name: donor.org_name || "",
    email: donor.email || "",
    phone: donor.phone || "",
    whatsapp: donor.whatsapp || "",
    address: donor.address || "",
    city: donor.city || "",
    country: donor.country || "Italia",
    referente: donor.referente || "",
    cf: donor.cf || "",
    piva: donor.piva || "",
    notes: donor.notes || "",
    consent_communications: donor.consent_communications !== false,
    consent_newsletter: donor.consent_newsletter !== false,
  };
}

export default function DonorFormDialog({
  open,
  onOpenChange,
  mode = "create",
  initialDonor = null,
  onSubmit,
  submitting = false,
}) {
  const [form, setForm] = useState(() => normalize(initialDonor));

  useEffect(() => {
    if (open) setForm(normalize(initialDonor));
  }, [open, initialDonor]);

  const isOrg = ORG_TYPES.includes(form.type);
  const isCompanyLike = form.type === "azienda" || form.type === "associazione" || form.type === "fondazione";

  function handleSubmit(e) {
    e.preventDefault();
    const payload = { ...form };
    // pulizia campi non applicabili
    if (!isOrg) {
      payload.org_name = null;
      payload.referente = null;
      payload.piva = null;
    } else {
      payload.first_name = null;
      payload.last_name = null;
      if (form.type === "chiesa") payload.piva = null; // le parrocchie generalmente non hanno P.IVA
    }
    // sbianca stringhe vuote (mantiene null coerente in DB)
    ["email","phone","whatsapp","address","city","cf","piva","referente","org_name","first_name","last_name","notes"].forEach((k) => {
      if (payload[k] === "") payload[k] = null;
    });
    onSubmit(payload);
  }

  const title = mode === "edit" ? "Modifica donatore" : "Nuovo donatore";
  const submitLabel = mode === "edit" ? "Salva modifiche" : "Salva";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500">Tipo</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid={DONORS.fieldType} className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="privato">Privato</SelectItem>
                <SelectItem value="chiesa">Chiesa</SelectItem>
                <SelectItem value="azienda">Azienda</SelectItem>
                <SelectItem value="associazione">Associazione</SelectItem>
                <SelectItem value="fondazione">Fondazione</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {form.type === "privato" ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nome</Label>
                <Input data-testid={DONORS.fieldFirstName} value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="mt-1.5"/>
              </div>
              <div>
                <Label>Cognome</Label>
                <Input data-testid={DONORS.fieldLastName} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="mt-1.5"/>
              </div>
            </div>
          ) : (
            <div>
              <Label>Denominazione</Label>
              <Input data-testid={DONORS.fieldOrg} value={form.org_name} onChange={(e) => setForm({ ...form, org_name: e.target.value })} className="mt-1.5"/>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Codice fiscale</Label>
              <Input data-testid="donor-field-cf" value={form.cf} onChange={(e) => setForm({ ...form, cf: e.target.value.toUpperCase() })} className="mt-1.5 mono uppercase tracking-wider"/>
            </div>
            {isCompanyLike && (
              <div>
                <Label>P.IVA</Label>
                <Input data-testid="donor-field-piva" value={form.piva} onChange={(e) => setForm({ ...form, piva: e.target.value })} className="mt-1.5 mono tracking-wider"/>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Email</Label>
              <Input data-testid={DONORS.fieldEmail} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5"/>
            </div>
            <div>
              <Label>Telefono</Label>
              <Input data-testid={DONORS.fieldPhone} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5"/>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>WhatsApp</Label>
              <Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className="mt-1.5"/>
            </div>
            <div>
              <Label>Città</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1.5"/>
            </div>
          </div>

          <div>
            <Label>Indirizzo</Label>
            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5"/>
          </div>

          {isOrg && (
            <div>
              <Label>Referente</Label>
              <Input value={form.referente} onChange={(e) => setForm({ ...form, referente: e.target.value })} className="mt-1.5" placeholder="Es. Don Marco / Città della casa"/>
            </div>
          )}

          <div>
            <Label>Note relazionali</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" rows={3}/>
          </div>

          <div className="flex items-center gap-6 pt-1">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.consent_communications} onCheckedChange={(v) => setForm({ ...form, consent_communications: !!v })} />
              Consenso comunicazioni
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.consent_newsletter} onCheckedChange={(v) => setForm({ ...form, consent_newsletter: !!v })} />
              Newsletter
            </label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" data-testid={DONORS.cancelBtn} onClick={() => onOpenChange(false)}>Annulla</Button>
            <Button type="submit" data-testid={DONORS.saveBtn} disabled={submitting} className="bg-[#1C352D] hover:bg-[#264A3E] transition-colors duration-150">
              {submitting ? "Salvataggio…" : submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
